import React, { useMemo, useState, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useLocation } from 'react-router-dom'; // or use your routing library
import { isTabletOrMobileSize } from '../../../utils/queries';
import NavbarContainer from '../../../components/NavbarContainer';
import { toast } from 'react-toastify';

import {
  Container,
  Title,
  Subtitle,
  Form,
  CustomizedTextField,
  SubmitButton,
} from './styles';

function isValidEmail(email: string) {
  return !email || /.+@.+\..+/.test(email);
}

function cleanPhoneDigits(input: string) {
  return input.replace(/\D/g, '').slice(0, 15);
}

function formatPhonePretty(value: string) {
  const digits = cleanPhoneDigits(value);
  const m = digits.match(/^(\d{0,3})(\d{0,3})(\d{0,4})(.*)$/);
  if (!m) return value;
  const [, a, b, c] = m;
  let out = '';
  if (a) out = `(${a}`;
  if (a && a.length === 3) out += ') ';
  if (b) out += b;
  if (b && b.length === 3) out += '-';
  if (c) out += c;
  return out;
}

const NYWaitlistPresaleForm: React.FC = () => {
  const isTabletOrMobile = useMediaQuery({ query: isTabletOrMobileSize });
  const location = useLocation();
  
  // Check if returning from successful payment
  const urlParams = new URLSearchParams(location.search);
  const sessionId = urlParams.get('session_id');
  const [showSuccess, setShowSuccess] = useState(!!sessionId);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    favoriteGym: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const canSubmit = useMemo(() => {
    return (
      !isSubmitting &&
      !!formData.firstName &&
      !!formData.lastName &&
      isValidEmail(formData.email) &&
      cleanPhoneDigits(formData.phone).length >= 10
    );
  }, [formData, isSubmitting]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type, checked } = e.target as any;
    if (name === 'phone') {
      setFormData((prev) => ({ ...prev, phone: formatPhonePretty(value) }));
      return;
    }
    if (type === 'checkbox') {
      setFormData((prev) => ({ ...prev, [name]: checked }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error('Please complete the form.');
      return;
    }
    setIsSubmitting(true);
    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: cleanPhoneDigits(formData.phone),
        favoriteGym: formData.favoriteGym || undefined,
      };

      const response = await fetch('https://backend-production-145782451072.us-central1.run.app/v1/waitlist/newyork/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to start checkout');
      }

      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (err) { 
      toast.error('Something went wrong. Please try again.'); 
      setIsSubmitting(false);
    }
  };

  // Success state
  if (showSuccess) {
    return (
      <>
        <NavbarContainer dashboardScreen={false} checkoutScreen={true} />
        <Container id="waitlist-success">
          <div style={{ 
            textAlign: 'center', 
            maxWidth: '600px', 
            margin: '0 auto',
            padding: '40px 20px'
          }}>
            {/* Success Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              backgroundColor: '#10b981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 30px',
            }}>
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="white"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>

            <Title>Welcome to the Waitlist! 🎉</Title>
            
            <Subtitle style={{ marginBottom: '30px' }}>
              Your spot is reserved. We'll notify you as soon as JackedRabbit launches in New York!
            </Subtitle>

            <div style={{
              backgroundColor: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '24px',
              marginTop: '30px',
              textAlign: 'left',
            }}>
              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                marginBottom: '16px',
                color: '#111827'
              }}>
                What's Next?
              </h3>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                color: '#6b7280',
                fontSize: '15px',
                lineHeight: '1.8',
              }}>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ marginRight: '12px', color: '#10b981', fontSize: '20px' }}>✓</span>
                  <span>You'll receive a confirmation email shortly</span>
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ marginRight: '12px', color: '#10b981', fontSize: '20px' }}>✓</span>
                  <span>We'll keep you updated on our New York launch progress</span>
                </li>
                <li style={{ marginBottom: '12px', display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ marginRight: '12px', color: '#10b981', fontSize: '20px' }}>✓</span>
                  <span>Your first month will be completely free when we launch</span>
                </li>
                <li style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <span style={{ marginRight: '12px', color: '#10b981', fontSize: '20px' }}>✓</span>
                  <span>You'll get priority access to book your first gym</span>
                </li>
              </ul>
            </div>

            <div style={{ marginTop: '40px' }}>
              <a
                href="https://jackedrabbitgyms.com"
                style={{
                  display: 'inline-block',
                  padding: '12px 32px',
                  backgroundColor: '#111827',
                  color: 'white',
                  textDecoration: 'none',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'background-color 0.2s',
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#111827'}
              >
                Back to Home
              </a>
            </div>

            <p style={{
              marginTop: '30px',
              color: '#9ca3af',
              fontSize: '14px',
            }}>
              Questions? Email us at <a href="mailto:support@jackedrabbitgyms.com" style={{ color: '#10b981', textDecoration: 'none' }}>support@jackedrabbitgyms.com</a>
            </p>
          </div>
        </Container>
      </>
    );
  }

  // Form state (default)
  return (
    <>
      <NavbarContainer dashboardScreen={false} checkoutScreen={true} />
      <Container id="waitlist">
        <Title>Join the New York Waitlist!</Title>
        <Subtitle>
          Join the waitlist to get your first month for just $1!
        </Subtitle>

        <Form onSubmit={handleSubmit}>
          <CustomizedTextField
            name="firstName"
            value={formData.firstName}
            onChange={handleChange}
            required
            label="First Name"
            placeholder="First Name"
            InputLabelProps={{ shrink: true }}
          />

          <CustomizedTextField
            name="lastName"
            value={formData.lastName}
            onChange={handleChange}
            required
            label="Last Name"
            placeholder="Last Name"
            InputLabelProps={{ shrink: true }}
          />

          <CustomizedTextField
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            label="Email"
            placeholder="you@example.com"
            InputLabelProps={{ shrink: true }}
          />

          <CustomizedTextField
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
            label="Phone Number"
            placeholder="(555) 123-4567"
            InputLabelProps={{ shrink: true }}
          />

          <CustomizedTextField
            name="favoriteGym"
            value={formData.favoriteGym}
            onChange={handleChange}
            label="Favorite NYC Gym/Studio (optional)"
            placeholder="Your favorite spot"
            InputLabelProps={{ shrink: true }}
          />

          <SubmitButton
            type="submit"
            style={{ borderRadius: 3, width: isTabletOrMobile ? 180 : 220, alignSelf: 'flex-end', marginTop: 30 }}
            disabled={!canSubmit || isSubmitting}
          >
            {isSubmitting ? 'Redirecting…' : 'Join NY Waitlist — $1'}
          </SubmitButton>
        </Form>
      </Container>
    </>
  );
};

export default NYWaitlistPresaleForm;